import apiEndpoints from '../../const/apiEndpoints'
import api from '../../services'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  GetSessionListResponse,
  GetSessionPayload,
  Session,
  ZoomUser,
  SaveSessionResponse
} from './types'
import ROUTES from '../../const/routes'
import history from '../../const/history'
import { RootState } from '../store'
import strings from '../../locale/en'
import { format } from 'date-fns'
import { DATE_FORMAT_YYYYMMDD, TIME_FORMAT_HHMM } from '../../const/dateFormat'

export const getSessionList = createAsyncThunk(
  'session/getSessionList',
  async (requestData: GetSessionPayload): Promise<GetSessionListResponse> => {
    const data = {
      ascDesc: null,
      pageSize: 10,
      sortBy: null,
      ...requestData
    }
    const {
      data: { data: sessionList, page, totalCount, totalPages }
    } = await api.post(`${apiEndpoints.getSessions}`, data)
    const responseData = {
      sessionList,
      pageNumber: page,
      totalElements: totalCount,
      totalPages
    }
    return responseData
  }
)

export const deleteSession = createAsyncThunk(
  'session/deleteSession',
  async (sessionId: string, { dispatch }): Promise<Array<Session>> => {
    const response = await api.get(
      `${apiEndpoints.deleteSession}?id=${sessionId}`
    )
    dispatch(getSessionList({ pageNo: 1 }))
    return response?.data
  }
)

export const getSessionById = createAsyncThunk(
  'session/getSession',
  async (sessionId: string): Promise<Session> => {
    const { data } = await api.get(`${apiEndpoints.getSession}?id=${sessionId}`)
    return data
  }
)

export const getZoomUsers = createAsyncThunk(
  'session/getZoomUsers',
  async (): Promise<ZoomUser> => {
    const { data } = await api.get(apiEndpoints.getZoomUsers)
    return data.data
  }
)

export const saveSession = createAsyncThunk(
  'session/saveSession',
  async (isEdit: boolean, { getState }): Promise<SaveSessionResponse> => {
    const {
      session: { formValues }
    } = getState() as RootState

    const {
      validationMessages: {
        field: { required },
        session: {
          selectInstitute,
          selectBranch,
          selectCourse,
          selectBatch,
          selectSubject,
          selectTeacher,
          selectPlatform,
          saveSessionFailed
        }
      }
    } = strings

    const {
      branchId,
      batchId,
      coachingCentreId,
      courseId,
      sessionName,
      sessionType,
      subjectId,
      teacherId,
      platform,
      webExUserId,
      zoomUserId,
      topicName,
      sessionDate,
      startTime,
      endTime,
      id
    } = formValues

    const formErrors: { [key: string]: any } = {
      coachingCentreId: coachingCentreId ? '' : selectInstitute,
      branchId: branchId?.[0]?.value ? '' : selectBranch,
      batchId: batchId?.[0]?.value ? '' : selectBatch,
      courseId: courseId ? '' : selectCourse,
      subjectId: subjectId ? '' : selectSubject,
      teacherId: teacherId ? '' : selectTeacher,
      platform: platform ? '' : selectPlatform,
      topicName: topicName ? '' : required
    }

    const canSubmit =
      Object.keys(formErrors).filter((key) => formErrors[key] !== '').length ===
      0

    if (canSubmit) {
      const requestPayload = {
        branchId,
        branchIds: [branchId[0].value],
        batchId,
        batchIds: [batchId[0].value],
        coachingCentreId,
        courseId,
        sessionDate: format(new Date(sessionDate), DATE_FORMAT_YYYYMMDD),
        startTime: format(startTime, TIME_FORMAT_HHMM),
        endTime: format(endTime, TIME_FORMAT_HHMM),
        sessionName,
        sessionType,
        subjectId,
        teacherId,
        platform,
        webExUserId,
        topicName,
        zoomUserId,
        ...(id && { id })
      }

      const response = isEdit
        ? await api.put(`${apiEndpoints.editSession}`, requestPayload)
        : await api.post(`${apiEndpoints.saveSession}`, requestPayload)
      if (response?.data) {
        history.push(ROUTES.SESSION_LIST)
        return response?.data
      }
      return { error: saveSessionFailed }
    } else {
      return { formErrors }
    }
  }
)
